import { Paper, TablePagination } from '@mui/material';
import Grid from '@mui/material/Grid';
import DashboardLayout from '@examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from '@examples/Navbars/DashboardNavbar';
import MDButton from '@components/MDButton';
import { useMaterialUIController } from 'context';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAlert } from '@components/showAlert/useAlertHook';
import { apiErrorMessage } from '@functions/api-error-message';
import { ProfileService } from './services/profile.service';
import { ModuleWithScreens, ProfileResponse } from './services/responses/profile.response';
import { ProfileHook } from './hooks/profile.hook';
import { ProfileFilter } from './components/profile.filter';
import { ProfileTable } from './components/profile.table';
import { ProfileModal } from './components/profile.modal';
import { environment } from 'routes/environment.router';

export const Profiles = () => {
    const { t } = useTranslation();
    const [controller] = useMaterialUIController();
    const { sidenavColor } = controller;
    const { showAlert } = useAlert();
    const service = new ProfileService();

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchFilter, setSearchFilter] = useState('');
    const [filterFinal, setFilterFinal] = useState('');
    const [refresh, setRefresh] = useState(true);
    const [total, setTotal] = useState(0);
    const [openModal, setOpenModal] = useState(false);
    const [editItem, setEditItem] = useState<ProfileResponse | null>(null);
    const [screenModules, setScreenModules] = useState<ModuleWithScreens[]>([]);
    const [profileActions, setProfileActions] = useState<string[]>([]);

    // orderBy vazio: a API ordena por nome e ignora o resto.
    const { json } = new ProfileHook({
        page,
        limit: rowsPerPage,
        orderBy: '',
        orderDirection: 'asc',
        filter: filterFinal,
        refresh,
        service,
    }).getJson();

    useEffect(() => {
        setTotal(json?.data?.totalItens ?? 0);
    }, [json]);

    // O agrupamento vem do servidor: sem ele a falha deve aparecer, não virar tela vazia.
    useEffect(() => {
        new ProfileService()
            .getScreens()
            .then((res) => setScreenModules((res.data.data ?? [])
                // Tela escondida pela flag: a permissão marcada não mudaria nada visível. Quem já tem continua tendo.
                .map((m) => ({
                    ...m,
                    screens: m.screens.filter((screen) => screen !== 'CustomFields' || environment.features.customFields),
                }))
                .filter((m) => m.screens.length > 0)))
            .catch((err) => showAlert(apiErrorMessage(err), 'error', 4000));

        new ProfileService()
            .getActions()
            .then((res) => setProfileActions(res.data.data ?? []))
            .catch((err) => showAlert(apiErrorMessage(err), 'error', 4000));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const search = () => {
        const term = searchFilter.trim();

        setPage(0);
        setFilterFinal(term ? `?${new URLSearchParams({ Search: term }).toString()}` : '');
    };

    const clearSearch = () => {
        setSearchFilter('');
        setFilterFinal('');
        setRefresh((p) => !p);
    };

    const getAll = () => setRefresh((p) => !p);

    const handleModal = () => {
        if (openModal) setEditItem(null);
        setOpenModal((p) => !p);
    };

    // A lista já traz as telas de cada perfil: não há endpoint por id.
    const editAction = (id: string) => {
        const profile = json?.data?.records.find((item) => item.id === id);

        if (!profile) return;

        setEditItem(profile);
        setOpenModal(true);
    };

    return (
        <DashboardLayout>
            <DashboardNavbar title={t('perfis.titulo')} />
            <Grid container spacing={1}>
                <ProfileFilter
                    searchFilter={searchFilter}
                    setSearchFilter={setSearchFilter}
                    onSearch={search}
                    onClear={clearSearch}
                    onEnter={(e) => e.key === 'Enter' && search()}
                />
                <Grid size={{ xs: 12, md: 2 }} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                    <MDButton
                        variant="gradient"
                        color={sidenavColor}
                        onClick={handleModal}
                        sx={{ height: 40, minWidth: 130, whiteSpace: 'nowrap' }}
                    >
                        {t('general.adicionar')}
                    </MDButton>
                </Grid>
            </Grid>
            <Paper sx={{ width: '100%', overflow: 'hidden', mt: 2 }}>
                <ProfileTable
                    data={json}
                    editAction={editAction}
                    refresh={refresh}
                    setRefresh={setRefresh}
                />
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    labelRowsPerPage={t('general.linhasPorPagina')}
                    labelDisplayedRows={({ from, to, count }) =>
                        `${from}-${to} ${t('general.X-de-X')} ${count !== -1 ? count : `${t('general.maisQue')} ${to}`}`
                    }
                    component="div"
                    count={total}
                    page={page}
                    onPageChange={(_, p) => setPage(p)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value));
                        setPage(0);
                    }}
                />
            </Paper>
            <ProfileModal
                open={openModal}
                edit={editItem}
                modules={screenModules}
                actions={profileActions}
                handleModal={handleModal}
                getAll={getAll}
                clearSearch={clearSearch}
            />
        </DashboardLayout>
    );
};
